import { Booking, Customer, Instructor } from '../types/crm';
import { QuoteWithDetails } from '../types/quotes';
import { PaymentStatusService } from './paymentStatusService';

export interface AdminNotification {
  id: string;
  type: 'warning' | 'info' | 'success' | 'error';
  title: string;
  message: string;
  action?: {
    label: string;
    url?: string;
    onClick?: () => void;
  };
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: 'financial' | 'operations' | 'data_quality' | 'performance';
  createdAt: string;
  dismissed?: boolean;
  autoResolve?: boolean; // Whether this notification can be auto-resolved
}

export interface NotificationInsights {
  overduePayments: {
    count: number;
    totalAmount: number;
    customers: Array<{
      id: string;
      companyName: string;
      overdueAmount: number;
      daysOverdue: number;
    }>;
  };
  instructorCostDiscrepancy: {
    skippedBookings: number;
    totalCost: number;
    bookings: Array<{
      id: string;
      courseName: string;
      instructorCost: number;
      customerName: string;
      issue: string;
    }>;
  };
  lowCompletionRate: {
    totalBookings: number;
    completedBookings: number;
    completionRate: number;
    threshold: number;
  };
  unpaidBookings: {
    count: number;
    totalAmount: number;
    recentBookings: Array<{
      id: string;
      courseName: string;
      courseDate: string;
      customerName: string;
      amount: number;
      daysSinceCourse: number;
    }>;
  };
  certificateIssues: {
    customersWithoutCertificates: number;
    certificatesWithoutPayment: number;
  };
}

export class AdminNotificationService {
  /**
   * Generate all admin notifications based on current data
   */
  static generateNotifications(
    customers: Customer[],
    bookings: Booking[],
    instructors: Instructor[],
    quotes?: QuoteWithDetails[]
  ): AdminNotification[] {
    const insights = this.analyzeBusinessData(customers, bookings);
    const dismissedIds = this.getDismissedNotifications();
    const notifications: AdminNotification[] = [];

    // Overdue payment notifications
    if (insights.overduePayments.count > 0 && !dismissedIds.has('overdue-payments')) {
      notifications.push({
        id: 'overdue-payments',
        type: 'warning',
        title: 'Overdue Payments Detected',
        message: `${insights.overduePayments.count} customers have overdue payments totaling €${insights.overduePayments.totalAmount.toLocaleString()}`,
        action: {
          label: 'View Overdue Customers',
          url: '/clients?filter=overdue'
        },
        priority: insights.overduePayments.totalAmount > 10000 ? 'high' : 'medium',
        category: 'financial',
        createdAt: new Date().toISOString(),
        autoResolve: false
      });
    }

    // Instructor cost discrepancy
    if (insights.instructorCostDiscrepancy.skippedBookings > 0 && !dismissedIds.has('instructor-cost-discrepancy')) {
      notifications.push({
        id: 'instructor-cost-discrepancy',
        type: 'info',
        title: 'Instructor Cost Discrepancy',
        message: `${insights.instructorCostDiscrepancy.skippedBookings} bookings have instructor costs (€${insights.instructorCostDiscrepancy.totalCost}) but no revenue - check for missing pricing or instructor assignments`,
        action: {
          label: 'Review Bookings',
          url: '/bookings'
        },
        priority: 'medium',
        category: 'financial',
        createdAt: new Date().toISOString(),
        autoResolve: false
      });
    }

    // Low completion rate
    if (insights.lowCompletionRate.completionRate < insights.lowCompletionRate.threshold && !dismissedIds.has('low-completion-rate')) {
      notifications.push({
        id: 'low-completion-rate',
        type: 'warning',
        title: 'Low Course Completion Rate',
        message: `Only ${insights.lowCompletionRate.completionRate.toFixed(1)}% of bookings are marked as completed (${insights.lowCompletionRate.completedBookings}/${insights.lowCompletionRate.totalBookings})`,
        action: {
          label: 'Review Bookings',
          url: '/bookings?status=completed'
        },
        priority: 'medium',
        category: 'operations',
        createdAt: new Date().toISOString(),
        autoResolve: false
      });
    }

    // Recent unpaid bookings (not completed, just old unpaid bookings)
    if (insights.unpaidBookings.count > 0) {
      const recentUnpaid = insights.unpaidBookings.recentBookings.filter(b => 
        b.daysSinceCourse > 7 && 
        b.daysSinceCourse < 30 // Only show bookings that are 7-30 days old
      );
      if (recentUnpaid.length > 0 && !dismissedIds.has('recent-unpaid-bookings')) {
        const recentUnpaidAmount = recentUnpaid.reduce((sum, b) => sum + b.amount, 0);
        notifications.push({
          id: 'recent-unpaid-bookings',
          type: 'warning',
          title: 'Recent Unpaid Bookings',
          message: `${recentUnpaid.length} bookings from 7-30 days ago are still unpaid (€${recentUnpaidAmount.toLocaleString()})`,
          action: {
            label: 'Review Unpaid Bookings',
            url: '/clients?filter=unpaid'
          },
          priority: 'medium',
          category: 'financial',
          createdAt: new Date().toISOString(),
          autoResolve: false
        });
      }
    }

    // Certificate issues
    if (insights.certificateIssues.customersWithoutCertificates > 0 && !dismissedIds.has('customers-without-certificates')) {
      notifications.push({
        id: 'customers-without-certificates',
        type: 'info',
        title: 'Customers Without Certificates',
        message: `${insights.certificateIssues.customersWithoutCertificates} customers have completed courses but no certificates generated`,
        action: {
          label: 'Review Certificates',
          url: '/clients?filter=inactive'
        },
        priority: 'low',
        category: 'operations',
        createdAt: new Date().toISOString(),
        autoResolve: false
      });
    }

    // Recently accepted quotes (within last 24 hours)
    if (quotes && quotes.length > 0) {
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      const recentlyAcceptedQuotes = quotes.filter(quote => {
        if (quote.status !== 'accepted') return false;
        
        // Support both snake_case and camelCase for acceptedDate
        const acceptedDateStr = quote.acceptedDate || (quote as any).accepted_date;
        if (!acceptedDateStr) return false;
        
        const acceptedDate = new Date(acceptedDateStr);
        if (isNaN(acceptedDate.getTime())) return false;
        
        return acceptedDate >= oneDayAgo;
      });

      if (recentlyAcceptedQuotes.length > 0) {
        recentlyAcceptedQuotes.forEach((quote) => {
          const notificationId = `quote-accepted-${quote.id}`;
          
          // Skip if this quote notification was already dismissed
          if (dismissedIds.has(notificationId)) {
            return;
          }
          
          const quoteNumber = (quote as any).quote_number || quote.quoteNumber;
          const customerName = quote.customer?.companyName || (quote.customer as any)?.company_name || 'Customer';
          const amount = Number((quote as any).total_amount || quote.totalAmount || 0);
          
          notifications.push({
            id: notificationId,
            type: 'success',
            title: `Quote Accepted: ${quoteNumber}`,
            message: `${customerName} accepted quote ${quoteNumber} for €${amount.toFixed(2)}. ${quote.acceptedBy ? `Accepted by: ${quote.acceptedBy}` : ''}`,
            action: {
              label: 'View Quote',
              url: '/quotes'
            },
            priority: 'high',
            category: 'financial',
            createdAt: quote.acceptedDate || (quote as any).accepted_date || new Date().toISOString(),
            autoResolve: true // Auto-resolve after 7 days
          });
        });
      }
    }

    return notifications.sort((a, b) => {
      const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * Analyze business data to generate insights
   */
  private static analyzeBusinessData(
    customers: Customer[],
    bookings: Booking[]
  ): NotificationInsights {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Overdue payments analysis
    const overduePayments = {
      count: 0,
      totalAmount: 0,
      customers: [] as Array<{
        id: string;
        companyName: string;
        overdueAmount: number;
        daysOverdue: number;
      }>
    };

    // Instructor cost discrepancy analysis
    const instructorCostDiscrepancy = {
      skippedBookings: 0,
      totalCost: 0,
      bookings: [] as Array<{
        id: string;
        courseName: string;
        instructorCost: number;
        customerName: string;
      }>
    };

    // Completion rate analysis
    const totalBookings = bookings.length;
    const completedBookings = bookings.filter(b => b.status === 'completed').length;
    const completionRate = totalBookings > 0 ? (completedBookings / totalBookings) * 100 : 0;

    // Unpaid bookings analysis
    const unpaidBookings = {
      count: 0,
      totalAmount: 0,
      recentBookings: [] as Array<{
        id: string;
        courseName: string;
        courseDate: string;
        customerName: string;
        amount: number;
        daysSinceCourse: number;
      }>
    };

    // Certificate issues analysis
    const certificateIssues = {
      customersWithoutCertificates: 0,
      certificatesWithoutPayment: 0
    };

    // Analyze each customer
    customers.forEach(customer => {
      const customerBookings = bookings.filter(b => b.customerId === customer.id);
      const paymentStatus = PaymentStatusService.calculatePaymentStatus(customer, bookings);

      // Check for overdue payments
      const overdueBookings = customerBookings.filter(booking => {
        const isUnpaid = booking.status !== 'job_paid' && booking.status !== 'cancelled' && booking.status !== 'completed';
        const bookingDate = new Date(booking.courseDate);
        const isOverdue = bookingDate < thirtyDaysAgo;
        return isUnpaid && isOverdue;
      });

      if (overdueBookings.length > 0) {
        const overdueAmount = overdueBookings.reduce((sum, booking) => sum + (booking.totalPrice || 0), 0);
        const daysOverdue = Math.floor((new Date().getTime() - new Date(overdueBookings[0].courseDate).getTime()) / (1000 * 60 * 60 * 24));
        
        overduePayments.count++;
        overduePayments.totalAmount += overdueAmount;
        overduePayments.customers.push({
          id: customer.id,
          companyName: customer.companyName,
          overdueAmount,
          daysOverdue
        });
      }

      // Check for unpaid bookings
      const unpaidBookingsForCustomer = paymentStatus.unpaidBookings;
      unpaidBookings.count += unpaidBookingsForCustomer.length;
      unpaidBookings.totalAmount += paymentStatus.totalUnpaidAmount;

      unpaidBookingsForCustomer.forEach(booking => {
        const daysSinceCourse = Math.floor((new Date().getTime() - new Date(booking.courseDate).getTime()) / (1000 * 60 * 60 * 24));
        unpaidBookings.recentBookings.push({
          id: booking.id,
          courseName: booking.courseName,
          courseDate: booking.courseDate,
          customerName: customer.companyName,
          amount: booking.totalPrice || 0,
          daysSinceCourse
        });
      });

      // Check certificate issues
      const hasCertificates = customer.numCertsGenerated > 0 || customer.paymentHistory.some(p => p.certsCount > 0);
      const hasPaidBookings = paymentStatus.paidBookings.length > 0;
      
      if (hasPaidBookings && !hasCertificates) {
        certificateIssues.customersWithoutCertificates++;
      }
    });

    // Analyze bookings for instructor cost discrepancy
    bookings.forEach(booking => {
      const revenue = booking.totalPrice || 0;
      const cost = booking.instructorPrice || 0;
      const hasInstructor = !!booking.instructorId;
      
      if (revenue === 0 && cost > 0) {
        const customer = customers.find(c => c.id === booking.customerId);
        instructorCostDiscrepancy.skippedBookings++;
        instructorCostDiscrepancy.totalCost += cost;
        instructorCostDiscrepancy.bookings.push({
          id: booking.id,
          courseName: booking.courseName,
          instructorCost: cost,
          customerName: customer?.companyName || 'Unknown',
          issue: hasInstructor ? 'Missing revenue pricing' : 'Missing instructor assignment'
        });
      }
    });

    return {
      overduePayments,
      instructorCostDiscrepancy,
      lowCompletionRate: {
        totalBookings,
        completedBookings,
        completionRate,
        threshold: 20 // Alert if completion rate is below 20%
      },
      unpaidBookings,
      certificateIssues
    };
  }

  /**
   * Get notification count for display in bell icon
   */
  static getNotificationCount(notifications: AdminNotification[]): number {
    return notifications.filter(n => !n.dismissed).length;
  }

  /**
   * Get high priority notification count
   */
  static getHighPriorityCount(notifications: AdminNotification[]): number {
    return notifications.filter(n => !n.dismissed && (n.priority === 'high' || n.priority === 'critical')).length;
  }

  /**
   * Mark notification as dismissed (persistently in localStorage)
   */
  static dismissNotification(notifications: AdminNotification[], notificationId: string): AdminNotification[] {
    // Save dismissed notification ID to localStorage
    this.saveDismissedNotification(notificationId);
    
    return notifications.map(n => 
      n.id === notificationId ? { ...n, dismissed: true } : n
    );
  }

  /**
   * Save dismissed notification ID to localStorage
   */
  private static saveDismissedNotification(notificationId: string): void {
    try {
      const dismissed = this.getDismissedNotifications();
      dismissed.add(notificationId);
      localStorage.setItem('dismissed_admin_notifications', JSON.stringify(Array.from(dismissed)));
    } catch (error) {
      console.error('Error saving dismissed notification:', error);
    }
  }

  /**
   * Get dismissed notification IDs from localStorage
   */
  private static getDismissedNotifications(): Set<string> {
    try {
      const stored = localStorage.getItem('dismissed_admin_notifications');
      if (stored) {
        return new Set(JSON.parse(stored));
      }
      return new Set();
    } catch (error) {
      console.error('Error getting dismissed notifications:', error);
      return new Set();
    }
  }

  /**
   * Generate notifications for due and overdue tasks / reminders belonging to the current user.
   * Call this separately and merge results into the main notification list.
   */
  static generateTaskNotifications(
    tasks: Array<{ id: string; text: string; completed: boolean; scheduled_date?: string; scheduled_time?: string; priority?: string }>,
    reminders: Array<{ id: string; reminder_text: string; is_completed: boolean; reminder_date: string; reminder_time?: string; priority?: string; customer_name?: string }>
  ): AdminNotification[] {
    const now = new Date();
    const dismissedIds = this.getDismissedNotifications();
    const notifications: AdminNotification[] = [];

    // ── Tasks ────────────────────────────────────────────────────────────────
    const pendingTasks = tasks.filter(t => !t.completed && t.scheduled_date);

    const overdueTasks = pendingTasks.filter(t => {
      const dt = new Date(`${t.scheduled_date}${t.scheduled_time ? 'T' + t.scheduled_time : 'T00:00:00'}`);
      return dt < now;
    });

    const dueTodayTasks = pendingTasks.filter(t => {
      const today = now.toISOString().split('T')[0];
      if (t.scheduled_date !== today) return false;
      if (!t.scheduled_time) return true;
      const dt = new Date(`${t.scheduled_date}T${t.scheduled_time}`);
      return dt >= now;
    });

    if (overdueTasks.length > 0) {
      const id = 'tasks-overdue';
      if (!dismissedIds.has(id)) {
        notifications.push({
          id,
          type: 'error',
          title: 'Overdue Tasks',
          message: `You have ${overdueTasks.length} overdue task${overdueTasks.length !== 1 ? 's' : ''} that need attention.`,
          action: { label: 'View Tasks', url: 'tasks' },
          priority: 'high',
          category: 'operations',
          createdAt: now.toISOString(),
        });
      }
    }

    if (dueTodayTasks.length > 0) {
      const id = `tasks-due-today-${now.toISOString().split('T')[0]}`;
      if (!dismissedIds.has(id)) {
        notifications.push({
          id,
          type: 'warning',
          title: 'Tasks Due Today',
          message: `You have ${dueTodayTasks.length} task${dueTodayTasks.length !== 1 ? 's' : ''} due today.`,
          action: { label: 'View Tasks', url: 'tasks' },
          priority: 'medium',
          category: 'operations',
          createdAt: now.toISOString(),
        });
      }
    }

    // ── Reminders ────────────────────────────────────────────────────────────
    const pendingReminders = reminders.filter(r => !r.is_completed);

    const overdueReminders = pendingReminders.filter(r => {
      const dt = new Date(`${r.reminder_date}${r.reminder_time ? 'T' + r.reminder_time : 'T00:00:00'}`);
      return dt < now;
    });

    const dueTodayReminders = pendingReminders.filter(r => {
      const today = now.toISOString().split('T')[0];
      if (r.reminder_date !== today) return false;
      if (!r.reminder_time) return true;
      const dt = new Date(`${r.reminder_date}T${r.reminder_time}`);
      return dt >= now;
    });

    if (overdueReminders.length > 0) {
      const id = 'reminders-overdue';
      if (!dismissedIds.has(id)) {
        notifications.push({
          id,
          type: 'error',
          title: 'Overdue Reminders',
          message: `You have ${overdueReminders.length} overdue reminder${overdueReminders.length !== 1 ? 's' : ''}.`,
          action: { label: 'View Reminders', url: 'tasks' },
          priority: 'high',
          category: 'operations',
          createdAt: now.toISOString(),
        });
      }
    }

    if (dueTodayReminders.length > 0) {
      const id = `reminders-due-today-${now.toISOString().split('T')[0]}`;
      if (!dismissedIds.has(id)) {
        notifications.push({
          id,
          type: 'warning',
          title: 'Reminders Due Today',
          message: `You have ${dueTodayReminders.length} reminder${dueTodayReminders.length !== 1 ? 's' : ''} due today.`,
          action: { label: 'View Reminders', url: 'tasks' },
          priority: 'medium',
          category: 'operations',
          createdAt: now.toISOString(),
        });
      }
    }

    return notifications;
  }

  /**
   * Get notifications by category
   */
  static getNotificationsByCategory(notifications: AdminNotification[], category: AdminNotification['category']): AdminNotification[] {
    return notifications.filter(n => n.category === category && !n.dismissed);
  }
}
